import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, BellRing, Save, ClipboardList, RefreshCw, Locate, MapPin, X, Download, Upload, Loader2, ScanLine, Share2, Copy, QrCode, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ImageUploadField from "@/components/ImageUploadField";
import FramedImage from "@/components/FramedImage";
import { useLang } from "@/context/LangContext";
import { QRCodeCanvas } from "qrcode.react";

export default function VendorDashboard() {
  const { t } = useLang();
  const [v, setV] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [notif, setNotif] = useState({ title: "", body: "", type: "offer" });
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    api.get("/vendors/mine/profile")
      .then((r) => setV(r.data))
      .catch((err) => {
        // 404 = caller has no vendor doc yet → handled in render below
        if (err?.response?.status !== 404) {
          // Network / auth error — surface via UI fallback (loaded stays true, v stays null)
        }
      })
      .finally(() => setLoaded(true));
  }, []);

  const refreshOrders = async () => {
    setOrdersLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data || []);
    } catch (e) {
      // ignore
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => { if (v) refreshOrders(); }, [v?.vendor_id]);

  const updateOrderStatus = async (order_id, status) => {
    try {
      await api.patch(`/orders/${order_id}`, { status });
      const statusLabel = t(`vd.status_${status}`) || status;
      toast.success(t("vd.order_marked").replace("{status}", statusLabel));
      refreshOrders();
    } catch (e) {
      toast.error(t("vd.order_update_failed"));
    }
  };

  if (!loaded) return <div className="p-12 text-center text-muted-foreground">{t("vd.loading")}</div>;
  if (!v) return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="font-heading text-2xl">{t("vd.no_profile_title")}</h1>
      <p className="text-muted-foreground mt-2">{t("vd.no_profile_text")}</p>
      <Button asChild className="mt-4 rounded-full"><a href="/become-vendor">{t("vd.no_profile_cta")}</a></Button>
    </div>
  );

  const save = async () => {
    try {
      const { data } = await api.put(`/vendors/${v.vendor_id}`, v);
      setV(data);
      toast.success(t("vd.profile_saved"));
    } catch (e) { toast.error(t("vd.save_failed")); }
  };

  const sendNotif = async () => {
    try {
      await api.post("/notifications", notif);
      toast.success(t("vd.notify_sent"));
      setNotif({ title: "", body: "", type: "offer" });
    } catch (e) { toast.error(t("vd.notify_blocked")); }
  };

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error(t("vd.geo_unsupported"));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setV((s) => ({
          ...s,
          lat: +pos.coords.latitude.toFixed(6),
          lng: +pos.coords.longitude.toFixed(6),
        }));
        toast.success(t("vd.location_captured"));
      },
      (err) => toast.error(err.code === 1 ? t("vd.geo_denied") : t("vd.geo_failed")),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
    );
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">{t("vd.title_eyebrow")}</div>
          <h1 className="font-heading text-3xl tracking-tighter">{v.business_name}</h1>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
            <Badge variant={v.is_approved ? "default" : "secondary"}>
              {v.is_approved ? t("vd.approved") : t("vd.awaiting_approval")}
            </Badge>
            <Badge variant={v.is_verified ? "default" : "outline"}>
              {v.is_verified ? t("vd.verified") : t("vd.unverified")}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("vd.open_now_toggle")}</span>
          <Switch checked={!!v.is_open_now} onCheckedChange={(x) => set("is_open_now", x)} data-testid="vendor-open-toggle" />
        </div>
      </div>

      <ShareStorePanel vendor={v} />

      <Tabs defaultValue="info" className="w-full">
        <TabsList>
          <TabsTrigger value="info" data-testid="vd-tab-info">{t("vd.tab_info")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="vd-tab-orders">{t("vd.tab_orders")}</TabsTrigger>
          <TabsTrigger value="notify" data-testid="vd-tab-notify">{t("vd.tab_notify")}</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6 space-y-6">
          <Card className="p-6 border-border/60">
            <h2 className="font-heading text-xl">{t("vd.business_info")}</h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.business_name")}</label>
            <Input value={v.business_name || ""} onChange={(e) => set("business_name", e.target.value)} className="mt-1" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.category")} <span className="text-primary/70 normal-case tracking-normal">· pick one primary + up to 5 more</span></label>
            <CategoryMultiPicker
              primary={v.category || ""}
              categories={v.categories || (v.category ? [v.category] : [])}
              onChange={({ primary, categories }) => {
                set("category", primary);
                set("categories", categories);
              }}
            />
            <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
              A vendor listed under <strong>Grocery &amp; FMCG</strong> will appear in <strong>Sab Kuch Fresh</strong>.
              Add <strong>Sweets &amp; Snacks</strong> too and you&apos;ll also surface in <strong>Khavayye</strong>.
              Add every category you actually stock — modern kirana shops carry it all.
            </p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.city")}</label>
            <Input value={v.city || ""} onChange={(e) => set("city", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.phone")}</label>
            <Input value={v.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.whatsapp")}</label>
            <Input value={v.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.website")}</label>
            <Input value={v.website || ""} onChange={(e) => set("website", e.target.value)} className="mt-1" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.address")}</label>
            <Input value={v.address || ""} onChange={(e) => set("address", e.target.value)} className="mt-1" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.description")}</label>
            <Textarea rows={4} value={v.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.working_hours")}</label>
            <Input value={v.working_hours || ""} onChange={(e) => set("working_hours", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.service_area")}</label>
            <Input value={v.service_area || ""} onChange={(e) => set("service_area", e.target.value)} className="mt-1" />
          </div>
          <div className="sm:col-span-2 rounded-xl border border-border/60 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="vendor-location-block">
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {t("vd.map_pin")}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {v.lat != null && v.lng != null
                  ? <>{t("vd.exact_pin")} <span className="font-mono" data-testid="vendor-current-coords">{Number(v.lat).toFixed(4)}, {Number(v.lng).toFixed(4)}</span></>
                  : <>{t("vd.using_city_centroid").replace("{city}", v.city || "")}</>
                }
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={captureLocation} data-testid="vendor-capture-location">
                <Locate className="h-3.5 w-3.5 mr-1" /> {v.lat != null ? t("vd.recapture") : t("vd.use_my_location")}
              </Button>
              {v.lat != null && (
                <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={async () => {
                  try {
                    const { data } = await api.delete(`/vendors/${v.vendor_id}/location`);
                    setV(data);
                    toast.success(t("vd.location_cleared"));
                  } catch (e) {
                    toast.error(t("vd.clear_failed"));
                  }
                }} data-testid="vendor-clear-location">
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
          <div>
            <ImageUploadField
              value={v.logo || ""}
              onChange={(url) => set("logo", url)}
              label={t("vd.logo")}
              testId="vendor-logo-upload"
              aspect="aspect-square"
            />
          </div>
          <div>
            <ImageUploadField
              value={v.banner || ""}
              onChange={(url) => set("banner", url)}
              label={t("vd.banner")}
              testId="vendor-banner-upload"
              aspect="aspect-video"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.offer_announcement")}</label>
            <Input value={v.offer || ""} onChange={(e) => set("offer", e.target.value)} className="mt-1" placeholder={t("vd.offer_placeholder")} />
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2 block">{t("vd.gallery")}</label>
              <span className="text-[11px] text-muted-foreground" data-testid="vendor-gallery-count">
                {t("vd.gallery_count").replace("{n}", String((v.gallery || []).length))}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(v.gallery || []).map((g, i) => (
                <div key={i} className="relative">
                  <FramedImage src={g} alt="" testId={`gallery-thumb-${i}`} />
                  <button
                    type="button"
                    onClick={() => set("gallery", (v.gallery || []).filter((_, idx) => idx !== i))}
                    data-testid={`gallery-remove-${i}`}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border text-xs z-10"
                  >×</button>
                </div>
              ))}
              {(v.gallery || []).length < 25 && (
                <ImageUploadField
                  value=""
                  onChange={(url) => url && set("gallery", [...(v.gallery || []), url])}
                  testId="vendor-gallery-upload"
                  aspect="aspect-square"
                />
              )}
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2 block">
                {t("vd.embedded_videos")}
              </label>
              <span className="text-[11px] text-muted-foreground" data-testid="vendor-videos-count">
                {t("vd.videos_count").replace("{n}", String((v.gallery_videos || []).length))}
              </span>
            </div>
            <div className="space-y-2">
              {(v.gallery_videos || []).map((u, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`vendor-video-row-${i}`}>
                  <Input
                    value={u}
                    onChange={(e) => set("gallery_videos", (v.gallery_videos || []).map((x, idx) => idx === i ? e.target.value : x))}
                    placeholder={t("vd.video_url_placeholder")}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => set("gallery_videos", (v.gallery_videos || []).filter((_, idx) => idx !== i))}
                    data-testid={`vendor-video-remove-${i}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(v.gallery_videos || []).length < 5 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => set("gallery_videos", [...(v.gallery_videos || []), ""])}
                  data-testid="vendor-video-add"
                >
                  {t("vd.add_video_link")}
                </Button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              {t("vd.subscription_note")}
            </p>
          </div>

          {/* ---------- Khavayye (Food) structured fields ---------- */}
          <KhavayyeFieldsBlock v={v} set={set} />

          {/* ---------- Soul Mates: Matrimonial vendor candidate roster ---------- */}
          <SoulMatesCandidatesBlock v={v} />

          {/* ---------- Products / inventory ---------- */}
          <div className="sm:col-span-2 border-t border-border/60 pt-5 mt-1" data-testid="vendor-products-editor">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("vd.products_label")}</label>
              <span className="text-[11px] text-muted-foreground" data-testid="vendor-products-count">
                {t("vd.products_count").replace("{n}", String((v.products || []).length))}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">{t("vd.products_hint")}</p>

            <VendorCsvTools
              vendorId={v.vendor_id}
              category={v.category}
              onImported={async () => {
                const { data } = await api.get("/vendors/mine/profile");
                setV(data);
              }}
            />

            <div className="grid grid-cols-1 gap-3">
              {(v.products || []).map((p, i) => {
                const imgs = (p.images && p.images.length ? p.images : [p.image || p.image_url || ""]).slice(0, 4);
                while (imgs.length < 4) imgs.push("");
                const setImgAt = (slotIdx, url) => set("products", (v.products || []).map((x, idx) => {
                  if (idx !== i) return x;
                  const next = [...(x.images && x.images.length ? x.images : [x.image || x.image_url || ""])].slice(0, 4);
                  while (next.length < 4) next.push("");
                  next[slotIdx] = url;
                  // keep legacy `image` field pointing at the first non-empty
                  const first = next.find(Boolean) || "";
                  return { ...x, images: next.filter(Boolean).slice(0, 4), image: first, image_url: first };
                }));
                return (
                  <div key={i} className="rounded-xl border border-border/60 p-3 space-y-3" data-testid={`vendor-product-row-${i}`}>
                    <div className="grid grid-cols-4 gap-2" data-testid={`vendor-product-images-${i}`}>
                      {imgs.map((url, k) => (
                        <div key={k} className="aspect-square" data-testid={`vendor-product-image-slot-${i}-${k}`}>
                          <ImageUploadField
                            value={url}
                            onChange={(v2) => setImgAt(k, v2)}
                            testId={`vendor-product-image-${i}-${k}`}
                            aspect="aspect-square"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input
                        value={p.name || ""}
                        onChange={(e) => set("products", (v.products || []).map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                        placeholder={t("vd.product_name_placeholder")}
                        data-testid={`vendor-product-name-${i}`}
                      />
                      <Input
                        value={p.price_range || ""}
                        onChange={(e) => set("products", (v.products || []).map((x, idx) => idx === i ? { ...x, price_range: e.target.value } : x))}
                        placeholder={t("vd.product_price_placeholder")}
                        data-testid={`vendor-product-price-${i}`}
                      />
                      <Input
                        value={p.size || ""}
                        onChange={(e) => set("products", (v.products || []).map((x, idx) => idx === i ? { ...x, size: e.target.value } : x))}
                        placeholder="Size (e.g. M / 500 g / 2 BHK)"
                        data-testid={`vendor-product-size-${i}`}
                      />
                      <Input
                        value={p.color || ""}
                        onChange={(e) => set("products", (v.products || []).map((x, idx) => idx === i ? { ...x, color: e.target.value } : x))}
                        placeholder="Colour / variant"
                        data-testid={`vendor-product-color-${i}`}
                      />
                    </div>
                    <Textarea
                      rows={2}
                      value={p.description || ""}
                      onChange={(e) => set("products", (v.products || []).map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                      placeholder={t("vd.product_desc_placeholder")}
                      data-testid={`vendor-product-desc-${i}`}
                    />
                    <div>
                      <Input
                        value={(p.tags || []).join(", ")}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Split on commas or spaces, keep hashtags intact, cap at 150 tokens
                          const parts = raw
                            .split(/[,\n]+/)
                            .map((s) => s.trim())
                            .filter(Boolean)
                            .slice(0, 150);
                          set("products", (v.products || []).map((x, idx) => idx === i ? { ...x, tags: parts } : x));
                        }}
                        placeholder="Tags — e.g. #saoji, #muttonmasala, #chickenmasala (max 150)"
                        data-testid={`vendor-product-tags-${i}`}
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Buyers can search by any tag. Comma-separated. Currently{" "}
                        <span className={`font-medium ${((p.tags || []).length) > 150 ? "text-destructive" : ""}`} data-testid={`vendor-product-tags-count-${i}`}>
                          {(p.tags || []).length}
                        </span>{" "}/ 150 tags.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => set("products", (v.products || []).filter((_, idx) => idx !== i))}
                      data-testid={`vendor-product-remove-${i}`}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> {t("vd.product_remove")}
                    </Button>
                  </div>
                );
              })}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 rounded-full"
              onClick={() => set("products", [...(v.products || []), { name: "", description: "", price_range: "", images: [], size: "", color: "" }])}
              data-testid="vendor-product-add"
            >
              {t("vd.product_add")}
            </Button>
          </div>
        </div>
        <Button onClick={save} className="mt-4 rounded-full" data-testid="vendor-save-btn"><Save className="h-4 w-4 mr-1" /> {t("vd.save")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <Card className="p-6 border-border/60">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-heading text-xl flex items-center gap-2">
                <ClipboardList className="h-5 w-5" /> {t("vd.orders_heading")}
              </h2>
              <Button
                onClick={refreshOrders}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="vd-orders-refresh"
                disabled={ordersLoading}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${ordersLoading ? "animate-spin" : ""}`} /> {t("vd.orders_refresh")}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {t("vd.orders_note")}
            </p>
            {orders.length === 0 ? (
              <div className="mt-6 text-sm text-muted-foreground text-center py-8" data-testid="vd-orders-empty">
                {ordersLoading ? t("vd.loading") : t("vd.orders_empty")}
              </div>
            ) : (
              <ul className="mt-4 divide-y divide-border/60" data-testid="vd-orders-list">
                {orders.map((o) => (
                  <li key={o.order_id} className="py-3 flex items-start gap-3" data-testid={`vd-order-${o.order_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-sm">{o.customer_name}</strong>
                        {o.customer_phone && <span className="text-xs text-muted-foreground">· {o.customer_phone}</span>}
                        <Badge variant={
                          o.status === "delivered" ? "default" :
                          o.status === "cancelled" ? "destructive" :
                          o.status === "in_progress" ? "secondary" : "outline"
                        }>{t(`vd.status_${o.status}`) || o.status}</Badge>
                      </div>
                      <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                        {(o.items || []).map((it, idx) => (
                          <li key={idx}>• {it.product_name || it.name} × {it.quantity || 1}</li>
                        ))}
                      </ul>
                      {o.notes && <div className="text-xs mt-1 italic text-muted-foreground">&ldquo;{o.notes}&rdquo;</div>}
                      <div className="text-[11px] text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <select
                      value={o.status}
                      onChange={(e) => updateOrderStatus(o.order_id, e.target.value)}
                      data-testid={`vd-order-status-${o.order_id}`}
                      className="h-8 text-xs rounded-md border border-input bg-background px-2"
                    >
                      <option value="new">{t("vd.status_new")}</option>
                      <option value="in_progress">{t("vd.status_in_progress")}</option>
                      <option value="delivered">{t("vd.status_delivered")}</option>
                      <option value="cancelled">{t("vd.status_cancelled")}</option>
                    </select>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="notify" className="mt-6">
          <Card className="p-6 border-border/60">
            <h2 className="font-heading text-xl flex items-center gap-2"><BellRing className="h-5 w-5" /> {t("vd.notify_heading")}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("vd.notify_subtitle")}
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <Input placeholder={t("vd.notify_title_placeholder")} value={notif.title} onChange={(e) => setNotif({ ...notif, title: e.target.value })} data-testid="notif-title-input" />
              <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={notif.type} onChange={(e) => setNotif({ ...notif, type: e.target.value })} data-testid="notif-type-select">
                <option value="offer">{t("vd.notify_type_offer")}</option>
                <option value="new_service">{t("vd.notify_type_service")}</option>
                <option value="update">{t("vd.notify_type_update")}</option>
                <option value="event">{t("vd.notify_type_event")}</option>
              </select>
              <Textarea rows={3} className="sm:col-span-2" placeholder={t("vd.notify_message")} value={notif.body} onChange={(e) => setNotif({ ...notif, body: e.target.value })} data-testid="notif-body-input" />
            </div>
            <Button onClick={sendNotif} className="mt-3 rounded-full" disabled={!notif.title || !notif.body} data-testid="notif-send-btn">
              <Sparkles className="h-4 w-4 mr-1" /> {t("vd.notify_send")}
            </Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}


/**
 * CategoryMultiPicker — chip-based multi-select for vendor categories.
 * The first chip in the array is always the "primary" (used on vendor
 * cards and category badges). Clicking a picked chip re-orders it to
 * primary; clicking the trash icon on a non-primary removes it.
 * Search box lets vendors filter the 28-item catalog quickly.
 * Cap at 6 total selections including the primary.
 */
function CategoryMultiPicker({ primary, categories, onChange }) {
  const [all, setAll] = React.useState([]);
  const [query, setQuery] = React.useState("");
  React.useEffect(() => {
    api.get("/categories").then(({ data }) => setAll(data || [])).catch(() => setAll([]));
  }, []);

  const selected = React.useMemo(() => {
    const list = Array.isArray(categories) ? [...categories] : [];
    // Ensure primary is at index 0 and present.
    const p = (primary || "").trim();
    if (p) {
      const rest = list.filter((c) => c && c !== p);
      return [p, ...rest];
    }
    return list;
  }, [primary, categories]);

  const emit = (next) => {
    const clean = next.filter((c, i) => c && next.indexOf(c) === i).slice(0, 6);
    const nextPrimary = clean[0] || "";
    onChange({ primary: nextPrimary, categories: clean });
  };

  const togglePick = (name) => {
    if (selected.includes(name)) {
      // Already picked → make it primary (or no-op if already primary)
      if (selected[0] === name) return;
      emit([name, ...selected.filter((c) => c !== name)]);
    } else {
      if (selected.length >= 6) {
        toast.info("Max 6 categories per store");
        return;
      }
      // First pick becomes primary automatically
      emit([...selected, name]);
    }
  };

  const removeAt = (name) => {
    emit(selected.filter((c) => c !== name));
  };

  const makePrimary = (name) => {
    if (selected[0] === name) return;
    emit([name, ...selected.filter((c) => c !== name)]);
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? all.filter((c) => c.name.toLowerCase().includes(q)) : all;

  return (
    <div className="mt-1.5" data-testid="vendor-category-multi-picker">
      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="vendor-category-selected">
        {selected.length === 0 && (
          <span className="text-xs text-muted-foreground italic px-1">No categories picked yet.</span>
        )}
        {selected.map((name, i) => (
          <span
            key={name}
            className={`inline-flex items-center gap-1 rounded-full text-xs px-2.5 py-1 border ${
              i === 0
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted/70 text-foreground/90 border-border/70"
            }`}
            data-testid={`vendor-category-chip-${i === 0 ? "primary" : "secondary"}-${i}`}
          >
            {i === 0 && <span className="text-[9px] font-bold uppercase tracking-wider opacity-90 mr-0.5">Primary</span>}
            <span className="whitespace-nowrap">{name}</span>
            {i !== 0 && (
              <button
                type="button"
                className="ml-0.5 opacity-60 hover:opacity-100 text-[10px] px-1 py-0.5 rounded hover:bg-background/70"
                onClick={() => makePrimary(name)}
                title="Make this the primary category"
                data-testid={`vendor-category-make-primary-${i}`}
              >
                ★
              </button>
            )}
            <button
              type="button"
              className="ml-0.5 opacity-60 hover:opacity-100"
              onClick={() => removeAt(name)}
              title={i === 0 ? "Remove primary (another will be promoted)" : "Remove"}
              data-testid={`vendor-category-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Search + picker grid */}
      <div className="rounded-xl border border-border/60 bg-background/50 p-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search categories… e.g. grocery, puja, ayurveda"
          className="h-8 text-xs mb-2"
          data-testid="vendor-category-search"
        />
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {filtered.map((c) => {
            const picked = selected.includes(c.name);
            return (
              <button
                type="button"
                key={c.slug}
                onClick={() => togglePick(c.name)}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                  picked
                    ? "bg-primary/15 border-primary/50 text-primary font-medium"
                    : "bg-transparent border-border/60 text-foreground/80 hover:bg-muted"
                }`}
                data-testid={`vendor-category-pick-${c.slug}`}
              >
                {picked ? "✓ " : "+ "}{c.name}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <span className="text-[11px] text-muted-foreground italic p-2">No matches.</span>
          )}
        </div>
      </div>
    </div>
  );
}



/**
 * ShareStorePanel — vendor-only panel with copy-link, native/WhatsApp
 * share and a printable QR code so vendors can hand out their store URL.
 * Non-members hitting the URL will see a members-only gate with a
 * "Login / Join" CTA on VendorProfile.
 */
function ShareStorePanel({ vendor }) {
  const [qrOpen, setQrOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  if (!vendor?.vendor_id) return null;
  const shareUrl = `${window.location.origin}/vendors/${vendor.vendor_id}`;
  const shareText = `Namaskar! Check out ${vendor.business_name} on sanskariBazarIn — our closed community marketplace.`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Store link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the link to copy");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: vendor.business_name, text: shareText, url: shareUrl });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

  const downloadQr = () => {
    const canvas = document.querySelector('[data-testid="vendor-share-qr-canvas"] canvas');
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `sanskaribazar-${(vendor.business_name || "store").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Card
      className="p-4 sm:p-5 border-primary/30 bg-gradient-to-br from-primary/5 via-primary/[0.02] to-transparent"
      data-testid="vendor-share-panel"
    >
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 shrink-0">
          <Share2 className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-primary font-semibold">Share your store</div>
              <div className="text-sm text-muted-foreground mt-0.5">
                Send this link to anyone — non-members will see an invite screen.
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-border/60 bg-background/80 px-3 py-2">
            <span
              className="text-xs sm:text-sm font-mono truncate flex-1 text-foreground/80"
              data-testid="vendor-share-url"
              title={shareUrl}
            >
              {shareUrl}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 shrink-0"
              onClick={copyLink}
              data-testid="vendor-share-copy-btn"
              aria-label="Copy link"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="rounded-full h-8"
              onClick={nativeShare}
              data-testid="vendor-share-native-btn"
            >
              <Share2 className="h-3.5 w-3.5 mr-1" /> Share…
            </Button>
            <Button
              asChild
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full h-8 border-green-600/40 text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
              data-testid="vendor-share-whatsapp-btn"
            >
              <a href={whatsappHref} target="_blank" rel="noreferrer">
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 mr-1 fill-current"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.16 5.335 5.495 0 12.05 0c3.181 0 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414 0 6.557-5.336 11.891-11.893 11.891-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.521.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                WhatsApp
              </a>
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full h-8"
              onClick={() => setQrOpen(true)}
              data-testid="vendor-share-qr-btn"
            >
              <QrCode className="h-3.5 w-3.5 mr-1" /> QR code
            </Button>
            <Button
              asChild
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-full h-8 text-muted-foreground"
              data-testid="vendor-share-preview-btn"
            >
              <a href={shareUrl} target="_blank" rel="noreferrer">Preview →</a>
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm" data-testid="vendor-share-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" /> Storefront QR
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Print this on your shop signage, visiting card or invoice — members can scan it to land straight on your store.
          </p>
          <div className="flex justify-center py-3" data-testid="vendor-share-qr-canvas">
            <div className="p-4 bg-white rounded-xl border border-border/60">
              <QRCodeCanvas
                value={shareUrl}
                size={220}
                includeMargin={false}
                level="M"
              />
              <div className="text-[10px] text-neutral-800 text-center font-medium mt-2 tracking-wide">
                {vendor.business_name}
              </div>
              <div className="text-[9px] text-neutral-500 text-center">sanskariBazarIn</div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setQrOpen(false)} data-testid="vendor-share-qr-close">
              Close
            </Button>
            <Button onClick={downloadQr} data-testid="vendor-share-qr-download">
              <Download className="h-4 w-4 mr-1" /> Download PNG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}



// Khavayye-specific structured fields. Only renders when the vendor's
// category is in the Khavayye whitelist so non-food vendors aren't bothered.
const KHAVAYYE_CATS = ["Food & Tiffin Services", "Sweets & Snacks", "Catering Services"];
const KHAVAYYE_CUISINES = ["Maharashtrian", "Konkani", "Marwadi", "Gujarati", "South Indian", "North Indian", "Punjabi", "Bengali", "Chinese"];
const KHAVAYYE_DIET = ["Pure Veg", "Jain", "Satvik", "No onion/garlic"];
const KHAVAYYE_DELIVERY = ["Home Delivery", "Pickup", "Dine-in", "On-site Catering", "Subscription"];

function KhavayyeFieldsBlock({ v, set }) {
  if (!KHAVAYYE_CATS.includes(v.category)) return null;
  const toggleArr = (key, value) => {
    const arr = Array.isArray(v[key]) ? v[key] : [];
    set(key, arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value]);
  };
  return (
    <div className="sm:col-span-2 border-t border-border/60 pt-5 mt-1" data-testid="vendor-khavayye-block">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Khavayye listing details</label>
        <Badge variant="secondary" className="text-[10px]">Food vertical</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        These fields power the Khavayye app — your business surfaces with the right cuisine/diet/delivery filters so consumers find you fast.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Cuisines</div>
          <div className="flex flex-wrap gap-1.5" data-testid="vendor-khavayye-cuisines">
            {KHAVAYYE_CUISINES.map((c) => {
              const on = (v.cuisines || []).includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleArr("cuisines", c)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-red-700 text-white border-red-700" : "border-border/60 hover:bg-muted"}`}
                  data-testid={`vendor-khavayye-cuisine-${c}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Diet</div>
          <div className="flex flex-wrap gap-1.5" data-testid="vendor-khavayye-diet">
            {KHAVAYYE_DIET.map((d) => {
              const on = (v.diet || []).includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleArr("diet", d)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-emerald-700 text-white border-emerald-700" : "border-border/60 hover:bg-muted"}`}
                  data-testid={`vendor-khavayye-diet-${d}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Service modes</div>
          <div className="flex flex-wrap gap-1.5" data-testid="vendor-khavayye-delivery">
            {KHAVAYYE_DELIVERY.map((d) => {
              const on = (v.delivery_modes || []).includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleArr("delivery_modes", d)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-orange-600 text-white border-orange-600" : "border-border/60 hover:bg-muted"}`}
                  data-testid={`vendor-khavayye-delivery-${d}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Avg cost for two (₹)</label>
          <Input
            type="number"
            value={v.avg_cost_for_two ?? ""}
            onChange={(e) => set("avg_cost_for_two", e.target.value === "" ? null : Number(e.target.value))}
            className="mt-1"
            placeholder="e.g. 400"
            data-testid="vendor-khavayye-avgcost"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Prep time (minutes)</label>
          <Input
            type="number"
            value={v.prep_time_min ?? ""}
            onChange={(e) => set("prep_time_min", e.target.value === "" ? null : Number(e.target.value))}
            className="mt-1"
            placeholder="e.g. 30"
            data-testid="vendor-khavayye-preptime"
          />
        </div>
        <label className="flex items-center justify-between cursor-pointer text-sm sm:col-span-2 rounded-xl border border-border/60 px-3 py-2">
          <span className="text-sm">Pure Veg only kitchen</span>
          <Switch
            checked={!!v.veg_only}
            onCheckedChange={(x) => set("veg_only", x)}
            data-testid="vendor-khavayye-veg-toggle"
          />
        </label>
        <div className="sm:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">FSSAI / Food licence number</label>
          <Input
            value={v.food_license || ""}
            onChange={(e) => set("food_license", e.target.value)}
            className="mt-1"
            placeholder="14-digit FSSAI number"
            data-testid="vendor-khavayye-license"
          />
        </div>
      </div>
    </div>
  );
}

// Compact roster manager for matrimonial vendors. Lets the office add
// candidate profiles (with consent attestation) and shows the current count.
// Single submit at a time — bulk CSV import is a future enhancement.
function SoulMatesCandidatesBlock({ v }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", gender: "female", age: 25, city: "", gotra: "", sub_caste: "",
    education: "", profession: "", height_cm: 165, income_range: "", diet: "Pure Veg",
    photo_url: "", bio: "", consent: false,
  });
  const isMatrimonial = v.category === "Matrimonial Services";

  useEffect(() => {
    if (!isMatrimonial) return;
    api.get("/matrimonial/profiles", { params: { page_size: 1 } })
      .then(() => api.get("/matrimonial/profiles", { params: { search: v.business_name, page_size: 1 } }))
      .then((r) => setCount(r?.data?.total || 0))
      .catch(() => {});
  }, [isMatrimonial, v.business_name]);

  if (!isMatrimonial) return null;

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please attest candidate consent");
    setBusy(true);
    try {
      await api.post("/matrimonial/profiles/bulk", [form]);
      toast.success("Candidate added");
      setForm({ ...form, name: "", age: 25, city: "", gotra: "", education: "", profession: "", photo_url: "", bio: "", consent: false });
      setCount((c) => c + 1);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add candidate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sm:col-span-2 border-t border-border/60 pt-5 mt-1" data-testid="vendor-soulmates-block">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Soul Mates candidate roster</label>
        <Badge variant="secondary" className="text-[10px]">Matrimonial vertical</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        Add candidate profiles under your office on Soul Mates. You must attest written consent
        from each candidate; profiles without consent are not shown.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="outline" className="text-xs">≈ {count} candidates listed</Badge>
        <Button type="button" onClick={() => setOpen(!open)} size="sm" variant="outline" className="rounded-full" data-testid="vendor-soulmates-toggle">
          {open ? "Cancel" : "+ Add candidate"}
        </Button>
      </div>
      {open && (
        <form onSubmit={submit} className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="vendor-soulmates-form">
          <Input data-testid="vsm-name" placeholder="Full name" required value={form.name} onChange={set("name")} />
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={form.gender} onChange={set("gender")} data-testid="vsm-gender">
            <option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
          </select>
          <Input data-testid="vsm-age" type="number" placeholder="Age" value={form.age} onChange={(e) => setForm((s) => ({ ...s, age: Number(e.target.value) }))} />
          <Input data-testid="vsm-city" placeholder="City" value={form.city} onChange={set("city")} />
          <Input data-testid="vsm-gotra" placeholder="Gotra" value={form.gotra} onChange={set("gotra")} />
          <Input data-testid="vsm-education" placeholder="Education" value={form.education} onChange={set("education")} />
          <Input data-testid="vsm-profession" placeholder="Profession" value={form.profession} onChange={set("profession")} />
          <Input data-testid="vsm-photo" placeholder="Photo URL" value={form.photo_url} onChange={set("photo_url")} />
          <Textarea className="sm:col-span-2" rows={2} placeholder="Bio" value={form.bio} onChange={set("bio")} data-testid="vsm-bio" />
          <label className="sm:col-span-2 flex items-start gap-2 cursor-pointer text-sm rounded-xl bg-purple-50 dark:bg-purple-950/20 p-3 border border-purple-200/60">
            <input type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => setForm((s) => ({ ...s, consent: e.target.checked }))} data-testid="vsm-consent" />
            <span>
              <strong>Consent attestation:</strong> I have written permission from this candidate
              to publish their profile on Soul Mates under {v.business_name}.
            </span>
          </label>
          <Button type="submit" disabled={busy || !form.consent || !form.name} className="sm:col-span-2 rounded-full" data-testid="vsm-submit">
            {busy ? "Adding…" : "Add candidate"}
          </Button>
        </form>
      )}
    </div>
  );
}


/**
 * VendorCsvTools — sample template + bulk-import buttons for the "Add
 * Item" section. The template is category-aware so a grocery vendor
 * downloads a grocery-shaped CSV (with pulses / dals / atta sample
 * rows), a restaurant vendor downloads a thali/tiffin-shaped CSV, etc.
 */
function VendorCsvTools({ vendorId, category, onImported }) {
  const fileRef = React.useRef(null);
  const scanRef = React.useRef(null);
  const [importing, setImporting] = React.useState(false);
  const [scanning, setScanning] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [report, setReport] = React.useState(null);
  // Editable preview after AI reads a handwritten list
  const [scanRows, setScanRows] = React.useState(null); // null | array
  const [scanChecked, setScanChecked] = React.useState({});

  const downloadTemplate = async () => {
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      const url = `${api.defaults.baseURL}/vendor-templates/csv${params.toString() ? "?" + params.toString() : ""}`;
      // Anchor click triggers native download.
      const a = document.createElement("a");
      a.href = url;
      a.download = `sanskaribazar-${(category || "items").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("Could not download the template");
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setReport(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/vendors/${vendorId}/products/bulk-import`, form);
      setReport(data);
      const im = data?.imported_count ?? 0;
      const er = data?.error_count ?? 0;
      if (im > 0) toast.success(`Imported ${im} item${im === 1 ? "" : "s"}${er ? ` · ${er} skipped` : ""}`);
      else toast.info(`0 items imported${er ? ` · ${er} rows had errors` : ""}`);
      if (im > 0 && onImported) await onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleScan = async (file) => {
    if (!file) return;
    setScanning(true);
    setScanRows(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/vendors/${vendorId}/products/scan-list`, form);
      const rows = (data?.items || []).map((r) => ({
        ...r,
        tags: (r.tags || []).join(", "),
        image_url: r.image_url || "",
        image_source: r.image_source || "",
      }));
      setScanRows(rows);
      const preselected = {};
      rows.forEach((_, i) => { preselected[i] = true; });
      setScanChecked(preselected);
      if (rows.length === 0) toast.info("Couldn't read any items — try a clearer photo");
      else {
        const correlated = rows.filter((r) => r.image_source === "correlated").length;
        const catSuffix = data?.category ? ` · tags tailored for ${data.category}` : "";
        const imgSuffix = correlated > 0 ? ` · ${correlated} photo${correlated === 1 ? "" : "s"} correlated` : "";
        toast.success(`Found ${rows.length} item${rows.length === 1 ? "" : "s"}${catSuffix}${imgSuffix}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the list");
    } finally {
      setScanning(false);
    }
  };

  const updateScanRow = (i, patch) => {
    setScanRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const saveScanned = async () => {
    const chosen = (scanRows || []).filter((_, i) => scanChecked[i] && (scanRows[i].name || "").trim());
    if (chosen.length === 0) { toast.error("Nothing selected"); return; }
    setSaving(true);
    try {
      const payload = {
        items: chosen.map((r) => ({
          name: r.name,
          description: r.description || "",
          price_range: r.price_range || "",
          size: r.size || "",
          color: r.color || "",
          tags: (r.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
          image_url: r.image_url || "",
        })),
      };
      const { data } = await api.post(`/vendors/${vendorId}/products/append-items`, payload);
      toast.success(`Added ${data.appended_count} item${data.appended_count === 1 ? "" : "s"} to inventory`);
      setScanRows(null);
      setScanChecked({});
      if (onImported) await onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/[0.03] p-3"
      data-testid="vendor-csv-tools"
    >
      <div className="text-[11px] uppercase tracking-[0.16em] text-primary font-semibold mr-2">
        Bulk options
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-full h-8"
        onClick={downloadTemplate}
        data-testid="vendor-csv-download-template"
      >
        <Download className="h-3.5 w-3.5 mr-1" /> Download sample CSV
        {category && <span className="ml-1 text-[10px] text-muted-foreground">({category})</span>}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv,application/vnd.ms-excel,application/csv,text/plain"
        hidden
        data-testid="vendor-csv-upload-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        size="sm"
        className="rounded-full h-8"
        onClick={() => fileRef.current?.click()}
        disabled={importing}
        data-testid="vendor-csv-upload-btn"
      >
        {importing ? (
          <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Importing…</>
        ) : (
          <><Upload className="h-3.5 w-3.5 mr-1" /> Bulk import CSV</>
        )}
      </Button>

      <input
        ref={scanRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,text/plain,text/csv,.txt,.csv,.tsv"
        hidden
        data-testid="vendor-scan-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleScan(f);
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-full h-8"
        onClick={() => scanRef.current?.click()}
        disabled={scanning}
        data-testid="vendor-scan-btn"
        title="Upload a photograph of a handwritten list OR a loose .txt / .csv file — AI will parse it into product rows"
      >
        {scanning ? (
          <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Reading list…</>
        ) : (
          <><ScanLine className="h-3.5 w-3.5 mr-1" /> Scan list (image or text)</>
        )}
      </Button>
      <p className="text-[10px] text-muted-foreground w-full mt-1">
        Download the template, fill in your items (keep the header row), then upload. Existing items are preserved — new rows are appended.
        <br />Or snap a photograph of a hand-written list <strong>or drop any messy .txt / .csv file</strong> — our AI will parse it into editable rows for you to review before saving.
      </p>
      {report && (
        <div className="w-full mt-2 rounded-lg bg-background border border-border/60 p-2 text-[11px]" data-testid="vendor-csv-report">
          <div className="font-medium">Import summary</div>
          <div className="text-muted-foreground">Imported: {report.imported_count} · Skipped: {report.error_count}</div>
          {report.errors && report.errors.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-destructive">First {report.errors.length} row errors</summary>
              <ul className="mt-1 pl-4 list-disc">
                {report.errors.map((er, i) => (
                  <li key={i}>Row {er.row}: {er.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <Dialog open={!!scanRows} onOpenChange={(o) => !o && setScanRows(null)}>
        <DialogContent className="max-w-4xl" data-testid="vendor-scan-review">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" /> Review scanned items
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Edit any field, uncheck rows you don&apos;t want, then click <strong>Save</strong> to append the rest to your inventory.
          </p>

          <div className="max-h-[55vh] overflow-y-auto border border-border/60 rounded-xl" data-testid="vendor-scan-rows">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="p-1 w-8"></th>
                  <th className="p-1 w-14">Photo</th>
                  <th className="p-1 text-left">Name *</th>
                  <th className="p-1 text-left w-24">Price</th>
                  <th className="p-1 text-left w-20">Size</th>
                  <th className="p-1 text-left">Tags (comma-sep)</th>
                  <th className="p-1 text-left">Description</th>
                </tr>
              </thead>
              <tbody>
                {(scanRows || []).map((r, i) => (
                  <tr key={i} className="border-t border-border/60" data-testid={`vendor-scan-row-${i}`}>
                    <td className="p-1 text-center">
                      <input
                        type="checkbox"
                        checked={!!scanChecked[i]}
                        onChange={(e) => setScanChecked((s) => ({ ...s, [i]: e.target.checked }))}
                        className="h-4 w-4"
                        data-testid={`vendor-scan-check-${i}`}
                      />
                    </td>
                    <td className="p-1 text-center align-middle">
                      {r.image_url ? (
                        <div className="relative group inline-block">
                          <img
                            src={r.image_url}
                            alt={r.name}
                            className="h-12 w-12 rounded-md object-cover border border-border/60"
                            loading="lazy"
                            data-testid={`vendor-scan-img-${i}`}
                          />
                          {r.image_source === "correlated" && (
                            <span
                              className="absolute -top-1 -right-1 rounded-full bg-primary text-primary-foreground text-[8px] px-1 py-0.5 font-semibold shadow"
                              title="AI matched this photo from your uploaded image"
                            >
                              AI
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="h-12 w-12 rounded-md bg-muted/50 inline-block" />
                      )}
                    </td>
                    <td className="p-1"><Input className="h-7 text-xs" value={r.name || ""} onChange={(e) => updateScanRow(i, { name: e.target.value })} data-testid={`vendor-scan-name-${i}`} /></td>
                    <td className="p-1"><Input className="h-7 text-xs" value={r.price_range || ""} onChange={(e) => updateScanRow(i, { price_range: e.target.value })} data-testid={`vendor-scan-price-${i}`} /></td>
                    <td className="p-1"><Input className="h-7 text-xs" value={r.size || ""} onChange={(e) => updateScanRow(i, { size: e.target.value })} data-testid={`vendor-scan-size-${i}`} /></td>
                    <td className="p-1"><Input className="h-7 text-xs" value={r.tags || ""} onChange={(e) => updateScanRow(i, { tags: e.target.value })} placeholder="#dal, #veg" data-testid={`vendor-scan-tags-${i}`} /></td>
                    <td className="p-1"><Input className="h-7 text-xs" value={r.description || ""} onChange={(e) => updateScanRow(i, { description: e.target.value })} data-testid={`vendor-scan-desc-${i}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setScanRows(null)} disabled={saving} data-testid="vendor-scan-cancel"><X className="h-4 w-4 mr-1" /> Cancel</Button>
            <Button
              onClick={saveScanned}
              disabled={saving || !Object.values(scanChecked).some(Boolean)}
              data-testid="vendor-scan-save"
            >
              {saving ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>) : (<><Save className="h-4 w-4 mr-1" /> Save {Object.values(scanChecked).filter(Boolean).length} to inventory</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

